import { useState } from 'react'
import { useReviewCommission, useReviewQueue } from 'src/hooks/useAdminAffiliates'
import { ReviewQueueItem } from 'src/types/Affiliate'
import { formatBps, formatCents, formatDay } from 'src/utils/affiliateFormat'
import { notify } from 'src/utils/notify'
import { CommissionKindBadge, errorMessage } from '../affiliate/labels'
import {
    Badge,
    Card,
    CardTitle,
    DangerButton,
    EmptyRow,
    ErrorText,
    InlineSpinner,
    Input,
    Muted,
    Pager,
    PrimaryButton,
    Row,
    Table,
    TableWrap,
} from '../affiliate/ui'
import { Actions, InlineLink, Mono, Negative, SmallButtonStyle, useClampPage } from './shared'

const QueueRow = ({ item, review }: { item: ReviewQueueItem; review: ReturnType<typeof useReviewCommission> }) => {
    const [note, setNote] = useState('')
    const { commission, affiliate, referral, evidence } = item
    const busy = review.isPending && review.variables?.id === commission.id

    const decide = (decision: 'clear' | 'reject') => {
        if (
            decision === 'reject' &&
            !window.confirm(
                'Reject this commission? This also takes back the referral’s other held commissions and blocks all future commissions from this referral.'
            )
        ) {
            return
        }
        review.mutate(
            { id: commission.id, decision, note: note.trim() || undefined },
            {
                onSuccess: () => notify(decision === 'clear' ? 'Commission cleared.' : 'Commission rejected.', 'success'),
                onError: (err) => notify(errorMessage(err), 'error'),
            }
        )
    }

    return (
        <tr>
            <td className="num">
                <Negative $negative={commission.amountCents < 0}>{formatCents(commission.amountCents)}</Negative>
                {commission.baseCents != null && (
                    <Muted style={{ fontSize: 12 }}>
                        {formatBps(commission.rateBps)} of {formatCents(commission.baseCents)}
                    </Muted>
                )}
            </td>
            <td>
                <CommissionKindBadge kind={commission.kind} />
            </td>
            <td>
                <Row $gap={4}>
                    {commission.flags.length > 0
                        ? commission.flags.map((flag) => (
                              <Badge key={flag} $tone="red">
                                  {flag}
                              </Badge>
                          ))
                        : '—'}
                </Row>
            </td>
            <td>
                {affiliate.code ? (
                    <InlineLink to={`/admin/affiliates/${affiliate.id}`}>{affiliate.code}</InlineLink>
                ) : (
                    <InlineLink to={`/admin/affiliates/${affiliate.id}`}>View</InlineLink>
                )}
            </td>
            <td style={{ overflowWrap: 'anywhere' }}>
                {referral ? (
                    <>
                        <div>{referral.email || referral.displayName}</div>
                        {referral.discordUsername && <Muted style={{ fontSize: 12 }}>@{referral.discordUsername}</Muted>}
                    </>
                ) : (
                    '—'
                )}
            </td>
            <td>
                {evidence.matchingOrderIds.length > 0 ? (
                    evidence.matchingOrderIds.map((id) => (
                        <div key={id}>
                            <Mono>{id}</Mono>
                        </div>
                    ))
                ) : (
                    <Muted>None</Muted>
                )}
            </td>
            <td style={{ whiteSpace: 'nowrap' }}>{formatDay(commission.createdAt)}</td>
            <td style={{ minWidth: 260 }}>
                <Actions>
                    <Input
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Note (optional)"
                        maxLength={500}
                        style={{ padding: '8px 10px', fontSize: 14 }}
                    />
                    <PrimaryButton style={SmallButtonStyle} disabled={busy} onClick={() => decide('clear')}>
                        Clear
                    </PrimaryButton>
                    <DangerButton style={SmallButtonStyle} disabled={busy} onClick={() => decide('reject')}>
                        Reject
                    </DangerButton>
                </Actions>
            </td>
        </tr>
    )
}

const ReviewQueueTab = () => {
    const [page, setPage] = useState(1)
    const { data, isLoading, error, isPlaceholderData } = useReviewQueue(page)
    useClampPage(page, data?.totalPages, setPage, isPlaceholderData)
    const review = useReviewCommission()

    return (
        <Card>
            <CardTitle>Review queue</CardTitle>
            <Muted>Commissions held because they look suspicious. Clearing releases them into the normal hold window.</Muted>
            {isLoading ? (
                <InlineSpinner />
            ) : error ? (
                <ErrorText>{errorMessage(error)}</ErrorText>
            ) : (
                <>
                    <TableWrap>
                        <Table>
                            <thead>
                                <tr>
                                    <th className="num">Amount</th>
                                    <th>Kind</th>
                                    <th>Flags</th>
                                    <th>Affiliate</th>
                                    <th>Referral</th>
                                    <th>Matching orders</th>
                                    <th>Created</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {data && data.results.length > 0 ? (
                                    data.results.map((item) => (
                                        <QueueRow key={item.commission.id} item={item} review={review} />
                                    ))
                                ) : (
                                    <EmptyRow colSpan={8}>Nothing held for review.</EmptyRow>
                                )}
                            </tbody>
                        </Table>
                    </TableWrap>
                    {data && <Pager page={data.page} totalPages={data.totalPages} onChange={setPage} />}
                </>
            )}
        </Card>
    )
}

export default ReviewQueueTab
